import { Box, CircularProgress, Grid2 as Grid, TextField } from '@mui/material';
import { sum } from 'lodash';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import IsMountedWrapper from '../../../utils/functions/isMountedWrapper';
import { BookingStore } from '../../../utils/stores/BookingStore';
import { ProductsStore, SessionValue } from '../../../utils/stores/ProductsStore';

interface SessionSelectProps {
    readonly isPackage?: boolean;
}

// Inline replacements for the old makeStyles classes. Kept as plain `sx` so the field
// can't flash unstyled during availability re-renders (legacy JSS sheets could detach).
const availabilitySx = {
    sectionItem: {
        display: { xs: 'flex', sm: 'block' },
        flexDirection: { xs: 'column' },
        alignItems: { xs: 'center' },
        width: { sm: '90%', md: 'auto' },
    },
    selectFields: {
        width: { xs: '100%', sm: '90%' },
        '& .MuiOutlinedInput-root': { height: 54 },
    },
    loadContainer: {
        width: { xs: '100%', sm: '90%' },
        height: 54,
        display: 'flex',
        backgroundColor: 'white',
        justifyContent: 'center',
        borderStyle: 'solid',
        alignItems: 'center',
        borderWidth: 1,
        borderRadius: '4px',
        borderColor: 'rgb(0 0 0 / 23%)',
    },
    menu: { width: 190 },
} as const;

export default function SessionSelect({ isPackage }: SessionSelectProps) {
    const bookingController = useContext(BookingStore);
    const productController = useContext(ProductsStore);
    const isMounted = IsMountedWrapper();
    const [localTourDisplay, setLocalTourDisplay] = useState<string>(
        isPackage && (JSON.stringify({ time: bookingController.packageBooking.data.tour.startTimeLocal }) || '')
    );
    // Catalogue load OR an in-flight availability fetch. Kept off the global product-image
    // skeletons so picking a date spins the time field only — not the page imagery.
    const showLoading = productController.isLoading || productController.availabilityLoading;
    const showPackageLoading =
        productController.availability.packageSessions &&
        !productController.availability.packageSessions.find((s) =>
            moment(s.startTime).isSame(productController.availability?.packageDepartureDate, 'day')
        );
    const departureDate = isPackage
        ? productController.availability.packageDepartureDate
        : productController.availability.departureDate;
    const sessionArray = isPackage
        ? productController.availability.packageSessions
        : productController.availability.sessions;

    const handletimechange = (time: any) => {
        isPackage
            ? bookingController.packageBooking.setData(
                  (prev) => ({
                      ...prev,
                      tour: {
                          ...prev.tour,
                          startTimeLocal: moment(time).utcOffset(10).toISOString(true),
                      },
                  }),
                  isMounted
              )
            : bookingController.setBooking(
                  (prev) => ({
                      ...prev,
                      tour: {
                          ...prev.tour,
                          startTimeLocal: moment(time).utcOffset(10).toISOString(true),
                      },
                  }),
                  isMounted
              );
    };

    const changeTourTime = (event) => {
        if (event.target.value) {
            const res = JSON.parse(event.target.value);
            const { time } = res;
            handletimechange(time);
            setLocalTourDisplay(event.target.value);
        }
    };

    const removeUnavailable = (sessions: SessionValue[]) => {
        const cutOffTime = moment().add(
            Number(
                isPackage
                    ? productController.packageProduct.data.minimumNoticeMinutes
                    : productController.product.minimumNoticeMinutes
            ),
            'm'
        );
        const availSessions =
            sessions && isPackage
                ? sessions
                      .filter(
                          (session) =>
                              moment(session.startTime).isAfter(cutOffTime) &&
                              session.seatsRemaining >
                                  sum(
                                      bookingController.packageBooking.data.quantities.map((q) => q.seatsUsed * q.value)
                                  )
                      )
                      .map((session) => ({ ...session, startTime: session.startTime }))
                : sessions
                      .filter((session) => moment(session.startTime).isAfter(cutOffTime))
                      .map((session) => ({ ...session, startTime: session.startTime }));
        return availSessions;
    };

    const displayByDay = (date: string, sessions: SessionValue[]) => {
        const localSessions = sessions?.map((session) => ({
            ...session,
            startTime: moment(session.startTime).utcOffset(10).toISOString(true),
        }));
        const dailySessions = localSessions
            ?.filter((session) => moment(session.startTime).isSame(moment(date), 'day'))
            .map((session) => ({ ...session, startTime: session.startTime }));
        return dailySessions;
    };

    return (
        <>
            {(isPackage ? showPackageLoading : showLoading) ? (
                <Grid size={{ xs: 12 }} sx={availabilitySx.sectionItem}>
                    <Box sx={availabilitySx.loadContainer}>
                        <CircularProgress
                            variant="indeterminate"
                            color="primary"
                            disableShrink
                            size={24}
                            thickness={4}
                        />
                    </Box>
                </Grid>
            ) : (
                <Grid size={{ xs: 12 }} sx={availabilitySx.sectionItem}>
                    <TextField
                        select
                        sx={availabilitySx.selectFields}
                        style={{ width: isPackage && '100%' }}
                        value={localTourDisplay}
                        error={
                            isPackage && bookingController.customerErrors.packageDate === 'Check your departure time'
                        }
                        helperText={
                            isPackage &&
                            bookingController.customerErrors.packageDate === 'Check your departure time' &&
                            bookingController.customerErrors.packageDate
                        }
                        onChange={changeTourTime}
                        SelectProps={{
                            native: true,
                            MenuProps: {
                                sx: availabilitySx.menu,
                            },
                        }}
                        variant="outlined"
                        disabled={showLoading}
                    >
                        <option value="">
                            {displayByDay(departureDate, sessionArray) &&
                            removeUnavailable(displayByDay(departureDate, sessionArray)).length === 0
                                ? 'No Availability'
                                : 'Select Time'}
                        </option>
                        {displayByDay(departureDate, sessionArray) &&
                            removeUnavailable(displayByDay(departureDate, sessionArray)).map((session) => {
                                const { seatsRemaining, startTime } = session;
                                if (session.seatsRemaining > 0) {
                                    return (
                                        <option value={JSON.stringify({ time: startTime })} key={session.id}>
                                            {`${moment(startTime)
                                                .utcOffset(10)
                                                .format('h:mm a')} - ${seatsRemaining} remaining`}
                                        </option>
                                    );
                                }
                                return (
                                    <option value={JSON.stringify({ time: startTime })} key={session.id}>
                                        {startTime !== 'Invalid date'
                                            ? `${moment(startTime).utcOffset(10).format('h:mm a')} - Sold Out`
                                            : 'No Departures Available'}
                                    </option>
                                );
                            })}
                    </TextField>
                </Grid>
            )}
        </>
    );
}
